import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  can,
  createChapter,
  type EmphasisRange,
  deleteChapter,
  bookKey,
  getBook,
  listChapters,
  renameChapter,
  reorderChapters,
  updateBook,
  type Chapter,
} from "../api";
import BackLink from "../components/BackLink";
import EditableTitle from "../components/EditableTitle";
import MutationError from "../components/MutationError";
import SheetHeader from "../components/SheetHeader";
import TextFileDrop from "../components/TextFileDrop";
import { fromMarkdown, titleFrom } from "../components/textfile";
import { useT } from "../i18n";
import { refetch } from "../refetch";
import { primaryOrg, useSession } from "../session";
import BookTools from "../studio/BookTools";
import ChapterRow from "../studio/ChapterRow";
import DragList from "../studio/DragList";
import EpubImport from "../studio/EpubImport";
import NarrateAll from "../studio/NarrateAll";
import LinkedBanner from "../studio/LinkedBanner";
import TranslationBanner, { useTranslation } from "../studio/TranslationBanner";
import { holdsBook } from "../studio/translation";

/**
 * BookPage is a book in the studio (`/studio/books/:id`): its working
 * chapters and everything that changes the book — rename, reorder, delete, and
 * the sheet that takes a new chapter. Readers get ReaderBookPage instead,
 * which has nothing to share with a to-do list but the chapter titles.
 *
 * The studio's page is the chapters. Adding one is a button that opens a
 * sheet, and the book's other tools — publishing, characters, settings — are
 * rows that open their own screens (BookToolPage). They used to be panels
 * stacked under a permanently open form, which on a phone was a long scroll
 * past six things to find any one of them.
 *
 * The rows sit *above* the chapter list, which is the part that scrolls: state
 * before length. A book of twenty chapters put every tool below all of them.
 */
export default function BookPage() {
  const { bookID = "" } = useParams();
  const { me, isPending: sessionPending } = useSession();
  const org = primaryOrg(me);
  const client = useQueryClient();
  const t = useT();

  // The working copy, cover included: the studio shows the cover the author
  // has set, not the one readers were published (ADR-0062).
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  // The working copy, not the published edition readers are served (ADR-0024).
  const chapters = useQuery({
    queryKey: ["chapters", bookID, "draft"],
    queryFn: () => listChapters(bookID, { draft: true }),
    // While anything is narrating the rows are the progress display.
    refetchInterval: (query) => (query.state.data?.items.some((c) => c.narrating) ? 3000 : false),
  });

  // Every change to the chapter list is also a change to what publishing would do.
  // Through `refetch`: a rename made while the page's first read of the
  // edition plan was in flight left the publish tool "up to date" (10-25).
  const invalidate = () => refetch(client, ["chapters", bookID], ["edition", bookID]);

  const add = useMutation({
    mutationFn: (input: { title: string; body: string; emphasis?: EmphasisRange[] }) =>
      createChapter(bookID, input),
    onSuccess: invalidate,
  });
  /**
   * Reordering, applied to the list before the server has agreed.
   *
   * Without it the row springs back to where it was dragged from and then
   * jumps to its new place a round trip later, which reads as a failed drag.
   * The old list is kept so a refusal puts it back, and `reorderFailed` says
   * so — this used to fail silently, which is how a reorder that never worked
   * at all went unnoticed for weeks.
   */
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderChapters(bookID, ids),
    onMutate: async (ids: string[]) => {
      const key = ["chapters", bookID, "draft"];
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<{ items: Chapter[] }>(key);
      if (previous) {
        const byID = new Map(previous.items.map((c) => [c.id, c]));
        const items = ids.map((id) => byID.get(id)).filter((c) => !!c);
        client.setQueryData(key, { ...previous, items });
      }
      return { previous, key };
    },
    onError: (_err, _ids, context) => {
      if (context?.previous) client.setQueryData(context.key, context.previous);
    },
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: deleteChapter,
    onSuccess: invalidate,
  });
  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => renameChapter(id, title),
    onSuccess: invalidate,
  });
  const retitle = useMutation({
    mutationFn: (title: string) => updateBook(bookID, { title }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["book", bookID] }),
  });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  // The text came from a Markdown file: its *…* become emphasis when saved
  // (ADR-0205), so what the author edits is still the file's text.
  const [markdown, setMarkdown] = useState(false);
  const [adding, setAdding] = useState(false);
  // An EPUB picked in the sheet replaces the one-chapter form with its
  // chapter list until it is added or put back.
  const [epub, setEpub] = useState<File | null>(null);
  // The row controls — rename, move, delete — are shown on request. Four
  // buttons on every row at phone width left the titles a third of the row.
  const [arranging, setArranging] = useState(false);

  // Permission to edit, which decides what the server would allow.
  const writable = can(org, "chapter.write") && book.data?.org_id === org?.id;
  // The studio route still refuses the tools for a book you do not own.
  const editing = writable;
  const items = chapters.data?.items ?? [];
  // A translation writes this book's chapters in order until it finishes or
  // is stopped, and the server refuses others meanwhile (ADR-0055).
  const translation = useTranslation(bookID);
  const held = holdsBook(translation.data);
  // A linked book's words, chapters, cast and pictures come from its source and
  // are refused by the server if edited by hand (ADR-0093). Leaving the controls
  // out is showing that rule, not keeping it — everything else about the book,
  // narration and publishing above all, stays the author's.
  //
  // **Leaving the controls out is not leaving the screens out.** The cast and
  // the cover are still the author's book to look at, and hiding the rows
  // entirely — which is what this did at first — answered "what does my
  // translation look like?" with a missing menu item.
  const derived = !!book.data?.linked_source_id;
  const authoring = editing && !derived;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <nav className="mb-6 flex items-center justify-between gap-3 text-sm">
        <BackLink to="/studio">{t.book.backToStudio}</BackLink>
        {/* The reader's page offers this page back (ReaderBookPage), so a
            creator is never stuck in either. */}
        {writable && (
          <Link
            to={`/books/${bookID}`}
            data-testid="view-as-reader"
            className="rounded-control border border-line px-2.5 py-1 text-xs text-ink-soft transition-colors hover:bg-sunk"
          >
            {t.book.viewAsReader}
          </Link>
        )}
      </nav>

      <header className="mb-8">
        {/* The title is the author's on a linked book too (ADR-0100) — editing,
            not authoring: a book's title is its name, the same kind of thing as
            a chapter's, while the author, description, language and cover stay
            out. A refresh keeps a rename. */}
        <EditableTitle
          value={book.data?.title ?? "…"}
          canEdit={editing && !!book.data}
          onSave={(title) => retitle.mutateAsync(title)}
          label={t.book.bookNoun}
          testId="book-title"
          className="text-2xl font-semibold tracking-tight"
        />
        {book.data?.author && (
          <p dir="auto" className="mt-0.5 text-sm text-muted">
            {book.data.author}
          </p>
        )}
      </header>

      {editing && <TranslationBanner bookID={bookID} />}
      {editing && book.data && <LinkedBanner book={book.data} />}

      {/* The tools go above the chapters, not below them.

          Each row exists for the line under it saying what still needs doing —
          an edition behind, a cast not extracted, search out of date — and on a
          book of twenty chapters that line sat below every one of them, so
          reaching any of it on a phone was a scroll past the whole book.

          The chapter list is not folded to make room. Folding it would hide the
          narration state the rows themselves carry, and the chapters are what
          this page is for; Add chapter, Edit list and Narrate all stay with the
          list they act on. */}
      {book.data && book.data.org_id === org?.id && (
        <BookTools
          bookID={bookID}
          t={t}
          canPublish={can(org, "book.publish")}
          canCharacters={can(org, "book.augment")}
          canCover={can(org, "book.update") || can(org, "book.augment")}
          canSettings={
            can(org, "narration.render") || can(org, "book.index") || can(org, "book.delete")
          }
          canNarrate={can(org, "narration.render")}
        />
      )}

      {items.length === 0 && !chapters.isPending && !held && (
        <p data-testid="no-chapters" className="mb-8 text-sm text-muted">
          {t.book.noChapters}
        </p>
      )}

      <MutationError
        className="mb-3"
        testId="reorder-error"
        error={reorder.error}
        fallback={t.book.reorderFailed}
      />

      {/* Not before the book and the session: whether a row leads to the
          studio or the reader depends on who owns it and who is looking, and
          rows drawn before either all led to the reader until it arrived — so
          a quick tap on a studio book left the studio. */}
      {items.length > 0 && book.data && !sessionPending && (
        <DragList
          items={items}
          disabled={!authoring || !arranging}
          onReorder={(ids) => reorder.mutate(ids)}
          testID="chapter-list"
        >
          {(chapter, _index, handle) => (
            <ChapterRow
              chapter={chapter}
              writable={authoring && arranging}
              renamable={editing && arranging}
              studio={editing}
              handle={handle}
              onDelete={() => remove.mutate(chapter.id)}
              onRename={(title) => rename.mutateAsync({ id: chapter.id, title })}
            />
          )}
        </DragList>
      )}

      {/* Not before the book, for the same reason the chapter rows are not
          (above): the tool rows render once the book has loaded, and they sit
          above this button. Drawn first, the button was pushed down the moment
          they arrived — which on a phone is a tap landing on nothing, and is
          what made the add-chapter e2e specs flake on mobile Safari. */}
      {/* Edit list stays on a linked book: a chapter's name is the author's
          there (ADR-0098 §1), so the list offers the rename and nothing else. */}
      {editing && book.data && (
        <div className="flex flex-wrap items-center gap-2">
          {!authoring ? null : held ? (
            <p data-testid="add-chapter-held" className="text-xs text-muted">
              {t.translation.held}
            </p>
          ) : (
            <button
              type="button"
              data-testid="add-chapter"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-sage px-3.5 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep"
            >
              <svg
                viewBox="0 0 24 24"
                className="size-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t.book.addChapter}
            </button>
          )}
          {items.length > 0 && (
            <button
              type="button"
              data-testid="edit-list"
              aria-pressed={arranging}
              onClick={() => setArranging((on) => !on)}
              className={`rounded-full border px-3.5 py-2 text-sm transition-colors ${
                arranging
                  ? "border-sage bg-sage/15 text-ink"
                  : "border-line text-ink-soft hover:bg-sunk"
              }`}
            >
              {arranging ? t.book.doneEditing : t.book.editList}
            </button>
          )}
        </div>
      )}

      {can(org, "narration.render") && book.data?.org_id === org?.id && items.length > 0 && (
        <NarrateAll bookID={bookID} items={items} />
      )}

      {editing && adding && (
        <div
          role="dialog"
          aria-label={t.book.addHeading}
          data-testid="new-chapter-sheet"
          className="fixed inset-x-0 bottom-0 z-40 max-h-[92vh] overflow-y-auto rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
        >
          <div className="mx-auto w-full max-w-3xl">
            <SheetHeader
              title={t.book.addHeading}
              onClose={() => {
                setAdding(false);
                setEpub(null);
              }}
              closeTestID="new-chapter-close"
            />
            {epub && (
              <div className="px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <EpubImport
                  key={`${epub.name}:${epub.size}:${epub.lastModified}`}
                  bookID={bookID}
                  file={epub}
                  onAdded={() => {
                    void invalidate();
                    setEpub(null);
                    setAdding(false);
                  }}
                  onCancel={() => setEpub(null)}
                />
              </div>
            )}
            <form
              hidden={!!epub}
              data-testid="new-chapter-form"
              className="space-y-3 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
              onSubmit={(e) => {
                e.preventDefault();
                if (!title.trim() || !body.trim()) return;
                add.mutate(
                  { title: title.trim(), ...(markdown ? fromMarkdown(body) : { body }) },
                  {
                    onSuccess: () => {
                      setTitle("");
                      setBody("");
                      setMarkdown(false);
                      setAdding(false);
                    },
                  },
                );
              }}
            >
              <TextFileDrop
                disabled={add.isPending}
                onEPUB={setEpub}
                onText={(text, meta) => {
                  setBody(text);
                  setMarkdown(/\.(md|markdown)$/i.test(meta.filename));
                  // Only when empty: a filename is a worse title than one somebody
                  // typed on purpose, so it fills a blank and never overwrites.
                  setTitle((current) => current.trim() || titleFrom(meta.filename));
                }}
              />

              <input
                dir="auto"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t.book.chapterTitle}
                aria-label={t.book.chapterTitle}
                data-testid="chapter-title"
                /* text-base: iOS zooms into any input under 16px on focus. */
                className="w-full rounded-control border border-line bg-surface px-3 py-2.5 text-base"
              />
              {/* dir="auto" on the textarea too: the caret and alignment have to
                  follow the script the author is actually typing. */}
              <textarea
                dir="auto"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder={t.book.pasteText}
                aria-label={t.book.chapterText}
                data-testid="chapter-body"
                className="w-full rounded-control border border-line bg-surface px-3 py-2.5 font-sans text-base leading-7"
              />
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={add.isPending || !title.trim() || !body.trim()}
                  data-testid="chapter-submit"
                  className="rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
                >
                  {t.book.addChapter}
                </button>
                <span className="text-xs text-muted" data-testid="char-count">
                  {t.common.characters([...body].length)}
                </span>
              </div>
              {add.isError && (
                <p
                  role="alert"
                  data-testid="chapter-error"
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {add.error instanceof Error ? add.error.message : t.book.addFailed}
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
